import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { CronExecution } from "../../api/settingsApi";

interface ExecutionsTableProps {
  executions: CronExecution[];
  dateFormatter: Intl.DateTimeFormat;
  sortBy: string;
  sortOrder: "asc" | "desc";
  onSort: (column: string) => void;
  // Filters
  walletFilter: string;
  platformFilter: string;
  statusFilter: string;
  fromDate: string;
  toDate: string;
  onWalletFilterChange: (value: string) => void;
  onPlatformFilterChange: (value: string) => void;
  onStatusFilterChange: (value: string) => void;
  onFromDateChange: (value: string) => void;
  onToDateChange: (value: string) => void;
  onClearFilters: () => void;
  // Pagination
  page: number;
  totalPages: number;
  totalExecutions: number;
  onPageChange: (page: number) => void;
  // Frequency control
  currentFrequency: number | null;
  frequencies: number[];
  frequencyLabel: string;
  isFrequencyDirty: boolean;
  isBusy: boolean;
  isPending: boolean;
  onFrequencyChange: (value: number) => void;
  onApplyFrequency: () => void;
}

const SortIndicator = ({ active, order }: { active: boolean; order: "asc" | "desc" }) => {
  if (!active) return null;
  return <span className="text-purple-400">{order === "asc" ? "↑" : "↓"}</span>;
};

const capitalizePlatform = (platform: string): string => {
  const names: Record<string, string> = {
    twitch: "Twitch",
    youtube: "YouTube",
    kick: "Kick",
    system: "System",
  };
  return names[platform.toLowerCase()] || platform;
};

const getUserDisplayName = (execution: CronExecution): string | null => {
  // Priority: userName > userEmail > userId
  if (execution.userName && execution.userName.trim()) {
    return execution.userName;
  }
  if (execution.userEmail && execution.userEmail.trim()) {
    return execution.userEmail;
  }
  if (execution.userId) {
    return `ID: ${execution.userId}`;
  }
  return null;
};

export const ExecutionsTable = ({
  executions,
  dateFormatter,
  sortBy,
  sortOrder,
  onSort,
  walletFilter,
  platformFilter,
  statusFilter,
  fromDate,
  toDate,
  onWalletFilterChange,
  onPlatformFilterChange,
  onStatusFilterChange,
  onFromDateChange,
  onToDateChange,
  onClearFilters,
  page,
  totalPages,
  totalExecutions,
  onPageChange,
  currentFrequency,
  frequencies,
  frequencyLabel,
  isFrequencyDirty,
  isBusy,
  isPending,
  onFrequencyChange,
  onApplyFrequency,
}: ExecutionsTableProps) => {
  const hasFilters =
    walletFilter || platformFilter !== "all" || statusFilter !== "all" || fromDate || toDate;

  return (
    <Card className="border-neutral-800 bg-neutral-900/50">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div>
          <CardTitle className="text-lg font-semibold text-neutral-100">
            Historial de ejecuciones
          </CardTitle>
          <CardDescription className="text-neutral-400">
            Últimas ejecuciones del cron según la configuración seleccionada.
          </CardDescription>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-neutral-500">Frecuencia:</span>
          <Select
            value={currentFrequency?.toString() ?? ""}
            onValueChange={(v) => onFrequencyChange(Number(v))}
            disabled={isBusy || isPending}
          >
            <SelectTrigger className="w-24 border-neutral-700 bg-neutral-800/50 text-neutral-100 h-8 text-xs">
              <SelectValue placeholder="..." />
            </SelectTrigger>
            <SelectContent className="border-neutral-700 bg-neutral-900">
              {frequencies.map((minutes) => (
                <SelectItem
                  key={minutes}
                  value={minutes.toString()}
                  className="text-neutral-200 focus:bg-neutral-800"
                >
                  {minutes % 60 === 0 ? `${minutes / 60}h` : `${minutes}min`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {isFrequencyDirty && (
            <Button
              onClick={onApplyFrequency}
              disabled={isPending}
              size="sm"
              className="bg-purple-600 text-white hover:bg-purple-700 h-8 text-xs px-2"
            >
              {isPending ? "..." : "Guardar"}
            </Button>
          )}
          {!isFrequencyDirty && <span className="text-xs text-purple-400">{frequencyLabel}</span>}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Filters */}
        <div className="grid gap-3 md:grid-cols-5">
          <Input
            type="text"
            placeholder="Buscar por address..."
            value={walletFilter}
            onChange={(e) => onWalletFilterChange(e.target.value)}
            className="border-neutral-700 bg-neutral-800/50 text-neutral-200 placeholder:text-neutral-500"
          />
          <Select value={platformFilter} onValueChange={onPlatformFilterChange}>
            <SelectTrigger className="border-neutral-700 bg-neutral-800/50 text-neutral-200">
              <SelectValue placeholder="Plataforma" />
            </SelectTrigger>
            <SelectContent className="border-neutral-700 bg-neutral-900">
              <SelectItem value="all" className="text-neutral-200 focus:bg-neutral-800">
                Todas las plataformas
              </SelectItem>
              <SelectItem value="twitch" className="text-neutral-200 focus:bg-neutral-800">
                Twitch
              </SelectItem>
              <SelectItem value="youtube" className="text-neutral-200 focus:bg-neutral-800">
                YouTube
              </SelectItem>
              <SelectItem value="kick" className="text-neutral-200 focus:bg-neutral-800">
                Kick
              </SelectItem>
              <SelectItem value="system" className="text-neutral-200 focus:bg-neutral-800">
                System
              </SelectItem>
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={onStatusFilterChange}>
            <SelectTrigger className="border-neutral-700 bg-neutral-800/50 text-neutral-200">
              <SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent className="border-neutral-700 bg-neutral-900">
              <SelectItem value="all" className="text-neutral-200 focus:bg-neutral-800">
                Todos los estados
              </SelectItem>
              <SelectItem value="true" className="text-neutral-200 focus:bg-neutral-800">
                Completado
              </SelectItem>
              <SelectItem value="false" className="text-neutral-200 focus:bg-neutral-800">
                Fallido
              </SelectItem>
            </SelectContent>
          </Select>
          <Input
            type="datetime-local"
            value={fromDate}
            onChange={(e) => onFromDateChange(e.target.value)}
            className="border-neutral-700 bg-neutral-800/50 text-neutral-200"
          />
          <Input
            type="datetime-local"
            value={toDate}
            onChange={(e) => onToDateChange(e.target.value)}
            className="border-neutral-700 bg-neutral-800/50 text-neutral-200"
          />
        </div>

        {hasFilters && (
          <Button
            variant="outline"
            size="sm"
            onClick={onClearFilters}
            className="border-neutral-700 text-neutral-300 hover:bg-neutral-700"
          >
            Limpiar filtros
          </Button>
        )}

        {executions.length === 0 ? (
          <div className="rounded-md border border-neutral-700 bg-neutral-800/50 p-4 text-sm text-neutral-400">
            Todavía no hay ejecuciones registradas.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-neutral-800 hover:bg-transparent">
                <TableHead
                  className="cursor-pointer text-neutral-400 hover:text-neutral-200"
                  onClick={() => onSort("executedAt")}
                >
                  <div className="flex items-center gap-1">
                    Fecha
                    <SortIndicator active={sortBy === "executedAt"} order={sortOrder} />
                  </div>
                </TableHead>
                <TableHead
                  className="cursor-pointer text-neutral-400 hover:text-neutral-200"
                  onClick={() => onSort("platform")}
                >
                  <div className="flex items-center gap-1">
                    Integración
                    <SortIndicator active={sortBy === "platform"} order={sortOrder} />
                  </div>
                </TableHead>
                <TableHead className="text-neutral-400">Usuario</TableHead>
                <TableHead
                  className="cursor-pointer text-neutral-400 hover:text-neutral-200"
                  onClick={() => onSort("success")}
                >
                  <div className="flex items-center gap-1">
                    Resultado
                    <SortIndicator active={sortBy === "success"} order={sortOrder} />
                  </div>
                </TableHead>
                <TableHead
                  className="cursor-pointer text-right text-neutral-400 hover:text-neutral-200"
                  onClick={() => onSort("durationMs")}
                >
                  <div className="flex items-center justify-end gap-1">
                    Duración
                    <SortIndicator active={sortBy === "durationMs"} order={sortOrder} />
                  </div>
                </TableHead>
                <TableHead
                  className="cursor-pointer text-right text-neutral-400 hover:text-neutral-200"
                  onClick={() => onSort("tokensRefreshed")}
                >
                  <div className="flex items-center justify-end gap-1">
                    Tokens
                    <SortIndicator active={sortBy === "tokensRefreshed"} order={sortOrder} />
                  </div>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {executions.map((execution) => {
                const userDisplay = getUserDisplayName(execution);
                const isSystemNoWork =
                  execution.workspaceName === "System" && execution.platform === "system";

                return (
                  <TableRow
                    key={`${execution.executedAt}-${execution.workspaceName}-${execution.platform}`}
                    className="border-neutral-800 hover:bg-neutral-800/50"
                  >
                    <TableCell className="text-neutral-300">
                      {dateFormatter.format(new Date(execution.executedAt))}
                    </TableCell>
                    <TableCell>
                      {isSystemNoWork ? (
                        <span className="text-sm italic text-neutral-500">
                          Sin trabajos por hacer
                        </span>
                      ) : (
                        <div className="flex flex-col">
                          <span className="font-medium text-neutral-100">
                            {execution.workspaceName}
                          </span>
                          <span className="text-xs text-neutral-500">
                            {capitalizePlatform(execution.platform)}
                          </span>
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      {userDisplay ? (
                        <div className="flex flex-col">
                          <span className="text-neutral-300">{userDisplay}</span>
                          {execution.userCreatedAt && (
                            <span className="text-xs text-neutral-500">
                              Creado: {dateFormatter.format(new Date(execution.userCreatedAt))}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-neutral-500">Sin usuario</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={execution.success ? "default" : "destructive"}
                        className={
                          execution.success
                            ? "bg-green-500/20 text-green-400"
                            : "bg-red-500/20 text-red-400"
                        }
                      >
                        {execution.success ? "Completado" : "Fallido"}
                      </Badge>
                      {execution.message && (
                        <span className="ml-2 text-xs text-neutral-500">{execution.message}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right text-neutral-300">
                      {execution.durationMs
                        ? `${(execution.durationMs / 1000).toFixed(2)}s`
                        : "N/A"}
                    </TableCell>
                    <TableCell className="text-right text-neutral-300">
                      {execution.tokensRefreshed.toLocaleString("es-AR")}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}

        {executions.length > 0 && totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-neutral-800 pt-4">
            <div className="text-sm text-neutral-400">
              Página {page} de {totalPages} · {totalExecutions} ejecuciones totales
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onPageChange(Math.max(1, page - 1))}
                disabled={page === 1}
                className="border-neutral-700 text-neutral-300 hover:bg-neutral-700"
              >
                Anterior
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onPageChange(Math.min(totalPages, page + 1))}
                disabled={page === totalPages}
                className="border-neutral-700 text-neutral-300 hover:bg-neutral-700"
              >
                Siguiente
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
